import type {
  Subscription,
  SubscriptionPaymentInterval,
  SubscriptionPaymentIntervalOption,
} from 'src/api/endpoints/storeApi.types';
import { subscriptionGetPaymentInterval } from 'src/utils/subscriptionGetPaymentInterval';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';

export const CAMPAIGN_PRICE_PREVIEW_STALE_TIME = Infinity;

export type PaymentIntervalSavings = {
  total: number;
  currency: string;
};

export function canShowChangePaymentIntervalBanner(
  subscription?: Subscription
) {
  return (
    !!subscription &&
    subscriptionHelpers.isStripe(subscription) &&
    subscription.managementType !== 'seats' &&
    subscription.access.canChangePaymentInterval
  );
}

export function hasPaymentInterval(
  subscription: Subscription | undefined,
  interval: SubscriptionPaymentInterval
) {
  return subscriptionGetPaymentInterval(subscription) === interval;
}

export function getPaymentIntervalOption(
  subscription: Subscription | undefined,
  interval: SubscriptionPaymentInterval
) {
  if (!subscriptionHelpers.isStripe(subscription)) {
    return undefined;
  }

  return subscription.paymentInformation?.paymentIntervalOptions?.find(
    (option) => option.interval === interval
  );
}

export function getBiennialSavings(
  subscription?: Subscription
): PaymentIntervalSavings | undefined {
  const monthlyOption = getPaymentIntervalOption(subscription, 'monthly');
  const biennialOption = getPaymentIntervalOption(subscription, 'biennial');

  if (!monthlyOption || !biennialOption) {
    return undefined;
  }

  if (!hasSameCurrency(monthlyOption, biennialOption)) {
    return undefined;
  }

  const total = monthlyOption.amount.total * 24 - biennialOption.amount.total;

  if (total <= 0) {
    return undefined;
  }

  return {
    total,
    currency: biennialOption.amount.currency,
  };
}

function hasSameCurrency(
  firstOption: SubscriptionPaymentIntervalOption,
  secondOption: SubscriptionPaymentIntervalOption
) {
  return (
    firstOption.amount.currency.toLowerCase() ===
    secondOption.amount.currency.toLowerCase()
  );
}
